<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import debounce from 'lodash.debounce';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from '#imports';

const model = defineModel<string[]>({
  default: [], // Ensure we always have an array
});
const isOpen = ref(false);
const selectRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

// Track dropdown position
const dropdownStyle = ref({
  top: '0px',
  left: '0px',
  width: '0px',
});

const props = defineProps({
  label: String,
  helpText: String,
  hideLabel: Boolean,
  options: {
    type: Array,
    default: () => [],
  },
  optionsLabel: {
    type: String,
    default: 'label',
  },
  optionsValue: {
    type: String,
    default: 'value',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  showSelected: {
    type: Boolean,
    default: true,
  },
  showCounter: {
    type: Boolean,
    default: false,
  },
  deselectable: {
    type: Boolean,
    default: true,
  },
  placeholder: String,
});

const formattedOptions = computed(() =>
  props.options
    .filter(option => option !== null && option !== undefined)
    .map((option) => ({
    label: option.name || option.title || option[props.optionsLabel],
    value: option.id || option[props.optionsValue],
  })),
);

const displayValue = computed(() => {
  // If no value is selected, show placeholder
  if (!model.value?.length) {
    return props.placeholder || 'Select';
  }

  // For multiple select
  if (props.multiple) {
    if (model.value.length === 0) {
      return props.placeholder || 'Select';
    }
    // Find first selected option
    const firstSelected = formattedOptions.value.find(
      (opt) => opt.value === model.value[0],
    );
    if (model.value.length === 1) {
      return firstSelected?.label;
    }
    // Show first option + count of additional selections
    return `${firstSelected?.label} (+${model.value.length - 1} more)`;
  }

  // For single select - show the selected option
  const selectedOption = formattedOptions.value.find(
    (opt) => opt.value === model.value[0],
  );
  return selectedOption?.label || props.placeholder || 'Select';
});

const updateDropdownPosition = () => {
  if (!triggerRef.value || !dropdownRef.value) return;

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const dropdownHeight = dropdownRef.value.offsetHeight;
  const windowHeight = window.innerHeight;
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

  // Check if there's enough space below
  const spaceBelow = windowHeight - triggerRect.bottom;
  const shouldDropUp =
    spaceBelow < dropdownHeight && triggerRect.top > dropdownHeight;

  dropdownStyle.value = {
    top: shouldDropUp
      ? `${triggerRect.top + scrollTop - dropdownHeight}px`
      : `${triggerRect.bottom + scrollTop}px`,
    left: `${triggerRect.left + scrollLeft}px`,
    width: `${triggerRect.width}px`,
  };
};

const debouncedUpdatePosition = debounce(updateDropdownPosition, 16);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const handleSelect = (value: string) => {
  if (props.multiple) {
    const currentValues = model.value || [];
    const valueIndex = currentValues.indexOf(value);

    if (valueIndex === -1) {
      model.value = [...currentValues, value];
    } else if (props.deselectable) {
      model.value = currentValues.filter((v) => v !== value);
    }
  } else {
    model.value = [value];
    isOpen.value = false;
  }
};

const clearSelection = () => {
  model.value = [];
};

onClickOutside(selectRef, () => {
  isOpen.value = false;
});

onMounted(() => {
  if (
    !model.value?.length &&
    formattedOptions.value.length &&
    !props.multiple
  ) {
    // Uncomment if you want to automatically select the first option
    // model.value = [formattedOptions.value[0].value]
  }

  window.addEventListener('scroll', debouncedUpdatePosition);
  window.addEventListener('resize', debouncedUpdatePosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateDropdownPosition);
  window.removeEventListener('resize', updateDropdownPosition);
});
</script>

<template>
  <ClientOnly>
    <div ref="selectRef" class="select-container">
      <label v-if="showSelected && !hideLabel" class="select-label">
        {{ label }}
      </label>

      <label
        ref="triggerRef"
        class="select-trigger"
        :class="{ rounded, 'is-open': isOpen }"
        @click="toggleDropdown"
      >
        <div class="select-value">
          {{ displayValue }}
        </div>

        <div class="select-indicator">
          <i class="ri-expand-up-down-line" />
        </div>

        <button
          v-if="multiple && model?.length"
          class="clear-button"
          @click.stop="clearSelection"
        >
          <i class="ri-close-circle-fill" />
        </button>
      </label>

      <Teleport to="body">
        <div
          v-show="isOpen"
          ref="dropdownRef"
          class="select-dropdown"
          :style="dropdownStyle"
        >
          <div class="select-options">
            <div
              v-for="option in formattedOptions"
              :key="option.value"
              class="select-option"
              :class="{
                'is-selected': model?.includes(option.value)
              }"
              @click="handleSelect(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
      </Teleport>

      <div v-if="helpText" class="helper-text">
        {{ helpText }}
      </div>
    </div>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/typography.pcss";
@import "@/assets/css/input.pcss";
.select-container {
  width: 100%;
  min-width: 10rem;
  position: relative;
  text-align: left;
}

.select-label {
  @mixin mp-input-label;
}

.select-trigger {
  @mixin mp-input;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding-right: 2.75rem;
  min-height: 2.25rem;
  /* margin: 0.5rem 0; */
  position: relative;

  &.rounded {
    border-radius: 2rem;
  }

  &.is-open {
    border-color: var(--color-indigo-500);
  }
}

.select-value {
  @mixin typeface-regular;
  font-family: 'Hanken Grotesk', sans-serif;
  max-height: 1.2rem;
  overflow: hidden;
  text-align: left;
}

.select-indicator {
  position: absolute;
  right: 0.75rem;
  color: var(--color-slate-400);

  i {
    font-size: 1rem;
  }
}

.clear-button {
  position: absolute;
  right: 2rem;
  background: transparent;
  border: none;
  color: var(--color-slate-400);
  cursor: pointer;
  padding: 0.25rem;

  i {
    font-size: 1rem;
  }
}

.select-dropdown {
  position: absolute;
  background: white;
  @mixin mp-input;
  padding: 0;
  border-radius: 0.5rem;
  max-height: 20rem;
  min-width: fit-content;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.select-option {
  padding: 0.75rem 0.75rem 0.75rem 1rem;
  cursor: pointer;
  @mixin typeface-regular;

  &:hover {
    background-color: var(--color-slate-100);
  }

  &.is-selected {
    background-color: var(--color-indigo-50);
  }
}

.helper-text {
  @mixin mp-helper-text;
}
</style>
